import { AlertTriangle, CalendarClock, CheckCircle2, Info, MapPin, Send, Siren, XCircle } from 'lucide-react';
import { datumLang, sichtbareFragen, type Vorlage } from '../../lib/mailBuilder/vorlagen';
import { SPORTSCHULEN } from '../../lib/sportschulen';
import {
  EILT_BIS_STUNDE, EILT_GRUND_MIN_WOERTER, EILT_GRUND_MIN_ZEICHEN, VORLAUF_WERKTAGE, eiltGrundFehler, werktageBis,
} from '../../lib/freigabe';
import type { MailUser } from '../../types';
import {
  eiltZuFrueh, erlaubteSchulen, fruehesterTermin, istKnapp, kontaktOptionen, wunschZeitpunkt, type Stand, type VorlaufKontext,
} from './modell';

type Props = {
  v: Vorlage;
  stand: Stand;
  user: MailUser | null;
  setSchulen: (schulen: string[]) => void;
  setAntwort: (id: string, wert: string) => void;
  aendern: (teil: Partial<Stand>) => void;
  zeigeFehler: boolean;
  vorlauf: VorlaufKontext;
};

function woerter(t: string): number {
  return t.replace(/\s+/g, ' ').trim().split(' ').filter((w) => /[\p{L}\p{N}]/u.test(w)).length;
}

export function SchrittFragen({ v, stand, user, setSchulen, setAntwort, aendern, zeigeFehler, vorlauf }: Props) {
  const erlaubt = erlaubteSchulen(user);
  const fest = erlaubt.length === 1;
  const fragen = sichtbareFragen(v, stand.antworten);
  const w = wunschZeitpunkt(stand);
  const { eingang, selbst } = vorlauf;
  const knapp = istKnapp(stand, eingang);
  const frueh = fruehesterTermin(eingang);
  const fruehEilt = fruehesterTermin(eingang, true);
  const zuFrueh = stand.eilt && eiltZuFrueh(stand, eingang);
  const grund = stand.eiltGrund.replace(/\s+/g, ' ').trim();
  const grundFehler = eiltGrundFehler(stand.eiltGrund);
  const fehlenZeichen = Math.max(0, EILT_GRUND_MIN_ZEICHEN - grund.length);
  const fehlenWoerter = Math.max(0, EILT_GRUND_MIN_WOERTER - woerter(grund));
  const optionen = kontaktOptionen(stand, user);

  const schuleUmschalten = (name: string) => {
    setSchulen(stand.schulen.includes(name) ? stand.schulen.filter((s) => s !== name) : [...stand.schulen, name]);
  };

  return (
    <>
      {/* 1. Sportschule */}
      <section className="as-abschnitt">
        <h3 className="as-abschnitt-titel"><MapPin size={16} aria-hidden="true" /> Für welche Sportschule ist die Mail?</h3>
        <p className="as-warum">
          Danach richten sich die Empfängergruppen, die Adresse unten in der Mail und die Signatur
          („Euer Randori-Pro Team …").
        </p>
        {fest ? (
          <p className="as-fest"><strong>{erlaubt[0].name}</strong> — Deine Sportschule.</p>
        ) : (
          <>
            <div className="chip-row" role="group" aria-label="Sportschulen">
              {erlaubt.map((s) => (
                <button
                  key={s.name}
                  type="button"
                  className={`chip${stand.schulen.includes(s.name) ? ' active' : ''}`}
                  aria-pressed={stand.schulen.includes(s.name)}
                  onClick={() => schuleUmschalten(s.name)}
                >
                  {s.name}
                </button>
              ))}
              {erlaubt.length === SPORTSCHULEN.length && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setSchulen(stand.schulen.length === SPORTSCHULEN.length ? [] : SPORTSCHULEN.map((s) => s.name))}
                >
                  {stand.schulen.length === SPORTSCHULEN.length ? 'Keine' : 'Alle Sportschulen'}
                </button>
              )}
            </div>
            {zeigeFehler && stand.schulen.length === 0 && <p className="field-error" role="alert">Bitte mindestens eine Sportschule wählen.</p>}
            {user?.role === 'studio_leiter' && erlaubt.length === 0 && (
              <div className="alert alert-warning" style={{ marginTop: '0.75rem' }}>
                <AlertTriangle size={16} aria-hidden="true" />
                <div>Im CRM ist Dir noch keine Sportschule zugeordnet. Bitte wende Dich an das Backoffice — ohne Sportschule lässt sich keine Mail vorbereiten.</div>
              </div>
            )}
          </>
        )}
      </section>

      {/* 2. Fragen der Vorlage */}
      <section className="as-abschnitt">
        <h3 className="as-abschnitt-titel"><Info size={16} aria-hidden="true" /> Was vorher stehen muss</h3>
        {fragen.map((f, nr) => {
          const antwort = stand.antworten[f.id];
          const o = f.optionen.find((x) => x.wert === antwort);
          const offen = zeigeFehler && !o;
          return (
            <fieldset key={f.id} className={`as-frage${offen ? ' as-frage--offen' : ''}`}>
              <legend className="as-frage-titel">{nr + 1}. {f.frage}</legend>
              <p className="as-warum"><strong>Warum wir fragen:</strong> {f.warum}</p>
              <div className="as-optionen">
                {f.optionen.map((opt) => (
                  <label key={opt.wert} className={`as-option${antwort === opt.wert ? ' aktiv' : ''}`}>
                    <input
                      type="radio"
                      name={`frage-${f.id}`}
                      value={opt.wert}
                      checked={antwort === opt.wert}
                      onChange={() => setAntwort(f.id, opt.wert)}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
              {o?.folge === 'hinweis' && o.text && (
                <div className="as-folge as-folge--hinweis"><AlertTriangle size={15} aria-hidden="true" /><span>{o.text}</span></div>
              )}
              {o?.folge === 'klaeren' && (
                <div className="as-folge as-folge--klaeren" role="status">
                  <XCircle size={15} aria-hidden="true" />
                  <span><strong>So geht die Mail nicht raus.</strong> {o.text} Du kannst trotzdem weiterarbeiten — einreichen geht erst, wenn das geklärt ist.</span>
                </div>
              )}
              {offen && <p className="field-error" role="alert">Bitte eine Antwort wählen.</p>}
            </fieldset>
          );
        })}
      </section>

      {/* 3. Wunschtermin */}
      <section className="as-abschnitt" data-tour="assistent-vorlauf">
        <h3 className="as-abschnitt-titel"><CalendarClock size={16} aria-hidden="true" /> Wann soll die Mail rausgehen?</h3>
        {selbst ? (
          <p className="as-warum">
            Du darfst ohne Freigabe versenden — die Vorlaufzeit der Redaktion gilt für Dich nicht. Die Mail geht zum
            Wunschtermin raus, sobald Du sie im letzten Schritt verschickst.
          </p>
        ) : (
          <p className="as-warum">
            Die Redaktion sichtet montags bis freitags und braucht <strong>{VORLAUF_WERKTAGE} Werktage</strong> — am Montag
            eingereicht, geht die Mail frühestens am Freitag raus; das Wochenende zählt nicht mit.
            Frühester Termin ohne „eilt": <strong>{datumLang(frueh)}</strong>.{' '}
            {stand.wunschDatum !== frueh && (
              <button type="button" className="zf-aendern" onClick={() => aendern({ wunschDatum: frueh })}>übernehmen</button>
            )}
          </p>
        )}
        <div className="as-zeile">
          <div className="form-group">
            <label className="form-label" htmlFor="wunsch-datum">Datum<span className="as-pflicht" aria-hidden="true"> *</span></label>
            <input id="wunsch-datum" type="date" className={`form-input${zeigeFehler && !w ? ' input-error' : ''}`}
              value={stand.wunschDatum} onChange={(e) => aendern({ wunschDatum: e.target.value })} aria-required="true" />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="wunsch-zeit">Uhrzeit</label>
            <input id="wunsch-zeit" type="time" className="form-input" value={stand.wunschZeit} onChange={(e) => aendern({ wunschZeit: e.target.value })} />
          </div>
        </div>

        {w && w.getTime() < Date.now() && (
          <div className="vorlauf-ampel knapp" role="alert"><XCircle size={18} aria-hidden="true" /><div>Dieser Termin liegt in der Vergangenheit.</div></div>
        )}
        {w && w.getTime() >= Date.now() && selbst && (
          <div className="vorlauf-ampel gut">
            <Send size={18} aria-hidden="true" />
            <div>Die Mail geht am {datumLang(stand.wunschDatum)} um {stand.wunschZeit} Uhr raus — oder sofort, wenn Du das im letzten Schritt wählst.</div>
          </div>
        )}
        {w && w.getTime() >= Date.now() && !selbst && !knapp && (
          <div className="vorlauf-ampel gut">
            <CheckCircle2 size={18} aria-hidden="true" />
            <div>Passt: {werktageBis(w, eingang)} Werktage Vorlauf bis zum Versand am {datumLang(stand.wunschDatum)} um {stand.wunschZeit} Uhr.</div>
          </div>
        )}
        {w && w.getTime() >= Date.now() && !selbst && knapp && (
          <div className="vorlauf-ampel knapp">
            <Siren size={18} aria-hidden="true" />
            <div style={{ flex: 1 }}>
              <strong>Das ist knapp.</strong> Bis zum Versand bleiben weniger als {VORLAUF_WERKTAGE} Werktage. Wenn es wirklich
              nicht bis {datumLang(frueh)} warten kann, markier die Mail als „eilt" und begründe es — sonst bitte einen
              späteren Termin wählen.
              <label className="checkbox-row" style={{ marginTop: '0.6rem' }}>
                <input type="checkbox" checked={stand.eilt} onChange={(e) => aendern({ eilt: e.target.checked })} />
                <span>Eilt</span>
              </label>
            </div>
          </div>
        )}
        {!selbst && !knapp && (
          <label className="checkbox-row" style={{ marginTop: '0.75rem' }}>
            <input type="checkbox" checked={stand.eilt} onChange={(e) => aendern({ eilt: e.target.checked })} />
            <span>Trotzdem als „eilt" markieren</span>
          </label>
        )}
        {!selbst && stand.eilt && (
          <div className="as-eilt">
            <p className="as-eilt-regel">
              <strong>So gilt „eilt":</strong> Was bis {EILT_BIS_STUNDE} Uhr an einem Werktag bei der Redaktion ist, geht noch am
              selben Tag raus — sonst am nächsten Werktag. Reichst Du jetzt ein, geht die Mail frühestens am <strong>{datumLang(fruehEilt)}</strong> raus.
            </p>
            {zuFrueh && (
              <div className="vorlauf-ampel knapp" role="alert">
                <XCircle size={18} aria-hidden="true" />
                <div>Auch mit „eilt" ist {datumLang(stand.wunschDatum)} zu früh. Bitte frühestens {datumLang(fruehEilt)} wählen.
                  {' '}<button type="button" className="zf-aendern" onClick={() => aendern({ wunschDatum: fruehEilt })}>übernehmen</button>
                </div>
              </div>
            )}
            <div className="form-group" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
              <label className="form-label" htmlFor="eilt-grund">Warum eilt es?<span className="as-pflicht" aria-hidden="true"> *</span></label>
              <textarea id="eilt-grund" className={`form-textarea${zeigeFehler && grundFehler ? ' input-error' : ''}`} rows={3}
                placeholder="z. B. Der Trainer hat sich heute früh krankgemeldet — das Training morgen um 17 Uhr fällt aus, die Familien müssen es vorher wissen."
                value={stand.eiltGrund} onChange={(e) => aendern({ eiltGrund: e.target.value })}
                aria-describedby="eilt-grund-hinweis" aria-invalid={(zeigeFehler && !!grundFehler) || undefined} />
              <p id="eilt-grund-hinweis" className={`form-hint${grundFehler && zeigeFehler ? ' as-zu-lang' : ''}`} aria-live="polite">
                {grundFehler
                  ? <>Ohne Begründung geht keine eilige Mail raus. Mindestens {EILT_GRUND_MIN_ZEICHEN} Zeichen und {EILT_GRUND_MIN_WOERTER} Wörter
                    {fehlenZeichen > 0 || fehlenWoerter > 0 ? <> — noch {fehlenZeichen > 0 ? `${fehlenZeichen} Zeichen` : ''}{fehlenZeichen > 0 && fehlenWoerter > 0 ? ' und ' : ''}{fehlenWoerter > 0 ? `${fehlenWoerter} ${fehlenWoerter === 1 ? 'Wort' : 'Wörter'}` : ''}</> : null}.
                    {' '}Was ist passiert, und warum kann es nicht warten?</>
                  : <>Reicht. Die Redaktion sieht die Begründung beim Prüfen.</>}
              </p>
            </div>
          </div>
        )}
      </section>

      {/* 4. Kontakt */}
      <section className="as-abschnitt">
        <h3 className="as-abschnitt-titel">Wohin sollen Fragen gehen?</h3>
        <p className="as-warum">
          Die Mail kommt von der Adresse no-reply@randori-pro.de. Die Adresse hier steht unten in der Mail
          („Bei Fragen …") — und wer doch antwortet, landet dort.
        </p>
        <select className="form-select" value={stand.kontaktEmail} onChange={(e) => aendern({ kontaktEmail: e.target.value })} aria-label="Adresse für Fragen">
          {!optionen.some((o) => o.email === stand.kontaktEmail) && <option value="">— bitte wählen —</option>}
          {optionen.map((o) => <option key={o.email} value={o.email}>{o.label}</option>)}
        </select>
      </section>
    </>
  );
}
